import React, { useState, useEffect, useMemo } from 'react';
import { MapProps } from './IMapProvider';
import { MapProvider, useMapProvider } from './MapContext';
import { mapConfig } from './utils/mapConfig';
import { useGeolocation } from '../../hooks/useGeolocation';
import { useReverseGeocode } from '../../hooks/useReverseGeocode';
import { useMapInteraction } from '../../hooks/useMapInteraction';

/**
 * Internal component that renders the actual map using the provider
 * This component handles ALL map-related logic internally:
 * - Geolocation
 * - Reverse geocoding
 * - Search radius control
 * - Map interactions (click, right-click)
 * - Custom markers
 *
 * It also notifies parent pages of map state changes so they can display
 * location info and controls in their own UI panels.
 */
const GeographicalMapInner: React.FC<MapProps> = (props) => {
  const { provider } = useMapProvider();

  // Internal state: search radius
  const [searchRadius, setSearchRadius] = useState(mapConfig.defaultRadius);

  // Internal state: user location (auto-detected)
  const { location: autoLocation, setLocation, geolocationAllowed } = useGeolocation();

  // Internal state: reverse geocoded address
  const locationAddress = useReverseGeocode(autoLocation);

  // Internal state: custom markers and map interaction handlers
  const { customMarkers, handleMapClick, handleMapRightClick } = useMapInteraction({ setLocation });

  // Convert Location object to tuple format for map (memoized to prevent re-render loops)
  const locationTuple: [number, number] | null = useMemo(
    () => autoLocation ? [autoLocation.lat, autoLocation.lng] : null,
    [autoLocation?.lat, autoLocation?.lng]
  );

  // Notify parent of map state changes so they can display in their UI
  useEffect(() => {
    if (props.onMapStateChange) {
      props.onMapStateChange({
        userLocation: locationTuple,
        locationAddress,
        searchRadius,
        onRadiusChange: setSearchRadius,
        geolocationAllowed,
      });
    }
  }, [locationTuple, locationAddress, searchRadius, geolocationAllowed, props.onMapStateChange]);

  // Prepare props for the map provider with all internal state
  const mapProviderProps = {
    places: props.places,
    onPlaceClick: props.onPlaceClick,
    getPlaceDetailUrl: props.getPlaceDetailUrl,
    center: props.center || locationTuple || mapConfig.defaultCenter,
    zoom: props.zoom || mapConfig.defaultZoom,
    userLocation: locationTuple,
    searchRadius,
    onRadiusChange: setSearchRadius,
    locationAddress,
    onMapClick: handleMapClick,
    onMapRightClick: handleMapRightClick,
    customMarkers,
    showRadiusCircle: true,
  };

  return provider.renderMap(mapProviderProps);
};

/**
 * GeographicalMap component - main interface for displaying maps
 * Pages should only use this component. All map implementation details are hidden.
 *
 * Simple usage:
 * <GeographicalMap places={places} onPlaceClick={handleClick} />
 */
const GeographicalMap: React.FC<MapProps> = (props) => {
  return (
    <MapProvider>
      <GeographicalMapInner {...props} />
    </MapProvider>
  );
};

export default GeographicalMap;

// Re-export types for convenience
export type { MapPlace, CustomMarker, MapProps, MapState } from './IMapProvider';